// Messaging a lead: pick the channel (WhatsApp, Instagram, email where the lead has one), pick a template — it is filled in with the
// lead's own words and the sender's name, and can be edited — then open the chat. HQ sends nothing itself: opening records the
// message on the timeline and the daily first-message count, and hands back the link to open it from.
//
// A blank window is opened synchronously on the click and pointed at the link once the api has answered, so a pop-up blocker does
// not eat it. Instagram has no way to prefill a message, so the text is copied to the clipboard as the profile opens. When the
// message went out some other way (WhatsApp Web already open, say) it can be recorded without opening anything.

import type { Lead, LeadMessageChannel } from '@clavian/api-client';
import { renderLeadTemplate } from '@clavian/domain/leads';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Dialog, Field, SegmentedControl, SelectField, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useSession } from '../../api/queries';
import { useProblem } from '../../api/use-problem';
import { Notice } from '../../ui';
import { LEAD_MESSAGE_CHANNEL_ORDER } from './labels';
import { useLead, useLeadQueue, useLeadTemplates, useOpenLeadOutreach } from './queries';

export interface MessageDialogProps {
  /** The lead to message; `undefined` keeps the dialog closed. */
  readonly leadId: string | undefined;
  readonly initialChannel?: LeadMessageChannel | undefined;
  readonly onClose: () => void;
}

/** The select's stand-in for "no template": an option cannot have an empty value. */
const OWN = 'own';

export function MessageDialog({ leadId, initialChannel, onClose }: MessageDialogProps): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={leadId !== undefined} onOpenChange={(open) => (open ? undefined : onClose())} size="lg" dirty="auto" title={t('leads.message.title')}>
      {leadId === undefined ? null : <MessageFlow leadId={leadId} initialChannel={initialChannel} onClose={onClose} />}
    </Dialog>
  );
}

function MessageFlow({ leadId, initialChannel, onClose }: { readonly leadId: string; readonly initialChannel: LeadMessageChannel | undefined; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const lead = useLead(leadId);
  if (lead.data === undefined) {
    return (
      <p className="console-subtle" aria-busy="true">
        {t('leads.lead.loading')}
      </p>
    );
  }
  return <MessageForm lead={lead.data} initialChannel={initialChannel} onClose={onClose} />;
}

function MessageForm({ lead, initialChannel, onClose }: { readonly lead: Lead; readonly initialChannel: LeadMessageChannel | undefined; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const problems = useProblem();
  const { show } = useToast();
  const session = useSession();
  const templates = useLeadTemplates(false);
  const queue = useLeadQueue();
  const outreach = useOpenLeadOutreach();
  const channels = LEAD_MESSAGE_CHANNEL_ORDER.filter((one) => one === 'whatsapp' || (one === 'instagram' && lead.instagram !== null) || (one === 'email' && lead.email !== null));
  const [channel, setChannel] = useState<LeadMessageChannel>(
    initialChannel !== undefined && channels.includes(initialChannel) ? initialChannel : lead.phoneMobile ? 'whatsapp' : (channels[1] ?? 'whatsapp'),
  );
  const [chosen, setChosen] = useState<string | undefined>(undefined);
  const [edited, setEdited] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  const forChannel = (templates.data?.data ?? []).filter((one) => one.channel === channel);
  // Until one is picked, the first template for the channel is the one in the box.
  const template = chosen === OWN ? undefined : (forChannel.find((one) => one.id === chosen) ?? (chosen === undefined ? forChannel[0] : undefined));
  const rendered =
    template === undefined
      ? undefined
      : renderLeadTemplate(template.body, {
          business: lead.businessName,
          contact: lead.contactName ?? '',
          city: lead.city ?? '',
          area: lead.area ?? '',
          hook: lead.hook ?? '',
          sender: session.data?.user.name ?? '',
        });
  const text = edited ?? rendered?.text ?? '';
  const missing = edited === undefined ? (rendered?.missing ?? []) : [];

  const destination = channel === 'whatsapp' ? lead.phone : channel === 'instagram' ? (lead.instagram === null ? null : `@${lead.instagram}`) : lead.email;
  const blocked = lead.doNotContact ? t('leads.lead.whatsAppDnc') : channel === 'whatsapp' && !lead.phoneMobile ? t('leads.message.noPhone') : undefined;
  const cap = queue.data;

  const submit = async (open: boolean) => {
    if (text.trim() === '') {
      setFailure(t('leads.message.empty'));
      return;
    }
    setFailure(undefined);
    // Opened now, on the click, and pointed at the chat once the api has answered.
    const win = !open || channel === 'email' ? null : window.open('about:blank', '_blank');
    if (win !== null) win.opener = null;
    if (open && channel === 'instagram') void navigator.clipboard?.writeText(text).catch(() => undefined);
    try {
      const done = await outreach.mutateAsync({ leadId: lead.id, body: { channel, ...(template === undefined ? {} : { templateId: template.id }), text: text.trim() } });
      const link = channel === 'whatsapp' ? done.whatsAppUrl : channel === 'instagram' ? done.instagramUrl : lead.email === null ? null : `mailto:${lead.email}?body=${encodeURIComponent(text.trim())}`;
      if (open && link !== null) {
        if (win !== null) win.location.href = link;
        else if (window.open(link, '_blank', 'noopener') === null) show({ tone: 'warn', title: t('leads.message.popupBlocked') });
      } else win?.close();
      const next = done.lead.nextFollowUpAt;
      show({
        tone: 'ok',
        title: open && channel === 'instagram' ? t('leads.message.copied') : next === null ? t('leads.message.recorded') : t('leads.message.recordedFollowUp', { date: format.date(next, 'medium') }),
      });
      onClose();
    } catch (error) {
      win?.close();
      setFailure(problems.text(error, { fallback: t('leads.message.failed') }));
    }
  };

  const copyMessage = () => {
    if (text.trim() === '') {
      setFailure(t('leads.message.empty'));
      return;
    }
    setFailure(undefined);
    void navigator.clipboard?.writeText(text.trim()).then(
      () => show({ tone: 'ok', title: t('leads.message.copiedPlain') }),
      () => show({ tone: 'warn', title: t('leads.message.copyFailed') }),
    );
  };

  const action = channel === 'whatsapp' ? t('leads.message.openWhatsApp') : channel === 'instagram' ? t('leads.message.openInstagram') : t('leads.message.openEmail');

  return (
    <form
      className="console-form console-hq-message"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit(true);
      }}
    >
      <p className="console-muted">{lead.businessName}</p>
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      {blocked === undefined ? null : (
        <Notice tone="warn" role="status">
          {blocked}
        </Notice>
      )}
      <div className="console-hq-message__top">
        <SegmentedControl<LeadMessageChannel>
          size="sm"
          label={t('leads.message.channel')}
          value={channel}
          onValueChange={(next) => {
            setChannel(next);
            setChosen(undefined);
            setEdited(undefined);
          }}
          options={channels.map((one) => ({ value: one, label: t(`leads.channel.${one}`) }))}
        />
        <p className="console-muted console-hq-message__to">
          {t('leads.message.to')}: <span className="console-num">{destination ?? '—'}</span>
        </p>
      </div>
      {forChannel.length === 0 ? (
        <p className="console-subtle">{t('leads.message.noTemplates')}</p>
      ) : (
        <SelectField
          label={t('leads.message.template')}
          options={[...forChannel.map((one) => ({ value: one.id, label: one.name })), { value: OWN, label: t('leads.message.noTemplate') }]}
          value={template?.id ?? OWN}
          onValueChange={(value) => {
            setChosen(value);
            setEdited(undefined);
          }}
        />
      )}
      <Field label={t('leads.message.body')} required>
        {(control) => (
          <textarea
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            className="clav-input console-hq-template-editor__body"
            rows={8}
            maxLength={2000}
            value={text}
            onChange={(event) => setEdited(event.target.value)}
          />
        )}
      </Field>
      {missing.length === 0 ? null : (
        <Notice tone="warn" role="status">
          {t('leads.message.missing', { names: missing.join(', ') })}
        </Notice>
      )}
      <div className="console-form__actions console-hq-message__footer">
        {lead.stage === 'new' && cap !== undefined ? <span className="console-muted">{t('leads.message.firstLeft', { left: cap.firstMessagesLeftToday, cap: cap.dailyCap })}</span> : <span />}
        <span className="console-hq-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('leads.message.cancel')}
          </Button>
          <Button type="button" variant="ghost" onClick={copyMessage}>
            {t('leads.message.copy')}
          </Button>
          <Button type="button" variant="secondary" disabled={blocked !== undefined || destination === null} busy={outreach.isPending} onClick={() => void submit(false)}>
            {t('leads.message.recordOnly')}
          </Button>
          <Button type="submit" variant="primary" disabled={blocked !== undefined || destination === null} busy={outreach.isPending}>
            {action}
          </Button>
        </span>
      </div>
    </form>
  );
}
